import { X } from 'lucide-react';
import { Dialog } from 'radix-ui';
import { forwardRef, useRef, type HTMLAttributes, type ReactNode } from 'react';

import { DialogFoot } from './dialogFoot.js';

import { cn } from '../lib/cn.js';
import { Button } from './Button.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MODAL — A CENTRED, MODAL DIALOG. `Drawer` IS THE SAME WIDGET, EDGE-ANCHORED.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Follows the five rules in `Button.tsx`. Everything below the API surface is Radix's
 * `Dialog`, and that is the point: the focus trap, the return of focus on close, Escape,
 * `aria-labelledby`/`aria-describedby` wiring and hiding the rest of the page from assistive
 * technology are all things this file must NOT re-implement. What it adds is tokens, a shape
 * that makes the accessible name impossible to omit, and three corrections where Radix's
 * defaults are wrong for this console.
 *
 * ── 1. `title` IS A REQUIRED PROP, AND THAT IS THE WHOLE API DECISION ───────
 * Radix sets `aria-labelledby` only if a `Dialog.Title` is actually mounted; with no Title it
 * emits `role="dialog"` and NO name at all. That failure is invisible to the person who wrote
 * it — the panel looks finished, the heading they hand-rolled with a `<div class="text-lg">`
 * is right there — and total for a screen-reader user, who is told "dialog" and nothing else
 * about what just took over the page. A required prop turns that from a bug nobody reports
 * into a compile error.
 *
 * `titleHidden` is therefore the ONLY way to lose the visible heading, and it is `sr-only` —
 * the name stays in the accessibility tree, it just stops being painted. The alternative that
 * every design system eventually offers, an optional `title`, means the name vanishes as a
 * side effect of a layout decision.
 *
 * The native `title` ATTRIBUTE is omitted from the inherited props for the same reason. It is
 * a tooltip hint: it never appears on touch, it is unreachable by keyboard, and it competes as
 * a name source. A caller who writes `title="Edit extension"` expecting a heading must get the
 * heading, not a hover string.
 *
 * ── 2. `onCloseAutoFocus` IS DELIBERATELY NOT FORWARDED ────────────────────
 * `onOpenAutoFocus`, `onEscapeKeyDown` and `onInteractOutside` are passed through, because a
 * record-detail pane with unsaved edits has a real need to intercept a dismissal.
 * `onCloseAutoFocus` is not, and it is the one that looks most harmless: the usual reason to
 * reach for it is to stop the page scrolling as focus returns, the usual implementation is
 * `(e) => e.preventDefault()`, and the result is that focus is restored to NOTHING. It lands
 * on `<body>`, the next Tab starts from the top of the document, and a keyboard user who
 * opened a drawer from row 40 of a call log is returned to the skip link. WCAG 2.4.3 (Focus
 * Order) fails silently and only for the people who cannot simply click where they left off.
 *
 * ── 3. THE FOCUS-RESTORE BUG RADIX HAS WHEN THERE IS NO TRIGGER ────────────
 * `DialogContentModal` restores focus with, in effect,
 * `onCloseAutoFocus: (e) => { e.preventDefault(); triggerRef.current?.focus(); }`. The
 * `preventDefault()` is UNCONDITIONAL, so it also cancels `FocusScope`'s own restore — which
 * is fine when a `Dialog.Trigger` exists and is a dead drop when one does not. A controlled
 * Modal opened from a row menu, a keyboard shortcut or a router transition has no trigger, so
 * `triggerRef.current` is `null`, nothing is focused, and focus is lost exactly as in §2.
 *
 * So when no `trigger` is rendered this component captures whatever had focus at open time
 * (in `onOpenAutoFocus`, which Radix dispatches BEFORE it moves focus into the scope) and
 * restores it itself. Proved by the "controlled, no trigger" test — which fails against plain
 * Radix.
 *
 * ── 4. NO `aria-modal="true"`, ON PURPOSE ──────────────────────────────────
 * Radix hides the rest of the document with real `aria-hidden` attributes (`aria-hidden`'s
 * `hideOthers`). That is the technique with the best support across browser/AT pairs.
 * `aria-modal` is the spec-pure answer and is still mishandled — most visibly by VoiceOver,
 * where it has swallowed content — and applying both means the page is hidden twice by two
 * mechanisms that can disagree on scope. One mechanism, the one that works.
 *
 * ── 5. THE CLOSE BUTTON IS NOT OPTIONAL, AND ITS NAME IS REAL TEXT ─────────
 * Escape is invisible and clicking the scrim is undiscoverable, so a visible, named dismiss
 * control is the only affordance that is available to everyone; there is no `showClose` prop
 * to turn it off. Its name comes from `sr-only` text rather than `aria-label` because a text
 * node is what in-page translation rewrites and what a `Ctrl+F`/voice-control match sees; an
 * `aria-label` stays English in a translated page while the button it names does not.
 *
 * ── 6. WHY THE SCRIM IS A CANVAS TINT AND NOT A BLACK WASH ─────────────────
 * There is no overlay token, and rule 4 (no raw colour, ever) is not suspended because a
 * scrim is "not really a colour". `bg-canvas/80` reads as the expected dim on dark and as a
 * veil on light; the panel is still unambiguously in front because of `shadow-lg` and its
 * border, not because of the scrim. If the design wants a true dim in both themes, that is a
 * new token in `tokens.css` — not a hard-coded black wash at 50% here.
 *
 * That anti-pattern is DESCRIBED rather than spelled as a class name on purpose. Tailwind 4
 * scans this file as plain text and cannot tell a comment from markup, so naming the forbidden
 * utility here compiles it into the shipped stylesheet — and the raw-colour class the rule
 * exists to forbid then silently WORKS everywhere in the app, which is the opposite of the
 * point. Measured: an earlier draft of this paragraph named the utility, and the rule for it
 * duly appeared in `dist/assets/shell-*.css`. Describe the anti-pattern; never spell it.
 *
 * ── ON ANIMATION ───────────────────────────────────────────────────────────
 * There is none. Radix unmounts the content on close, so an entrance-only transition would be
 * half a treatment, and a proper pair needs `@keyframes` in `styles.css`. Better absent than
 * lopsided.
 */

/**
 * `open`/`defaultOpen`/`onOpenChange` are re-declared rather than inherited so this file owns
 * its controlled/uncontrolled contract. `modal` is NOT exposed: `modal={false}` turns off the
 * focus trap and stops hiding the rest of the page, which is a different widget with a
 * different keyboard contract, not a variant of this one.
 */
export interface DialogFrameProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Required. Becomes the `Dialog.Title` and therefore the dialog's accessible name. */
  title: ReactNode;
  /**
   * Visually hide the heading while keeping it as the accessible name — for a pane whose
   * subject is already stated by the row that opened it. It is `sr-only`, never removal.
   */
  titleHidden?: boolean;
  /** Wired to `aria-describedby` by Radix, and only when it is actually present. */
  description?: ReactNode;
  descriptionHidden?: boolean;
  /**
   * The control that opens it, rendered `asChild` onto `Dialog.Trigger` — so it must be a
   * single element that forwards its ref and spreads its props. Every primitive here does.
   * Omit it for a dialog driven by external state; see §3 for what that costs and how it is
   * paid for.
   */
  trigger?: ReactNode;
  /** Actions, pinned below the scrolling body so they do not scroll out of reach. */
  footer?: ReactNode;
  /** The close button's accessible name. Say what closes when it is not obvious. */
  closeLabel?: string;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Redirect initial focus — e.g. onto the first field rather than the close button. */
  onOpenAutoFocus?: Dialog.DialogContentProps['onOpenAutoFocus'];
  /** `preventDefault()` to hold the dialog open — an unsaved-changes guard. */
  onEscapeKeyDown?: Dialog.DialogContentProps['onEscapeKeyDown'];
  onInteractOutside?: Dialog.DialogContentProps['onInteractOutside'];
  overlayClassName?: string;
  /** Class for the scrolling body region. `className` goes to the panel itself. */
  bodyClassName?: string;
  /**
   * Whether the shell's dialog-layer slot (`dialogLayer.tsx`: the call strip) renders at the
   * foot of this panel. On by default, so a call stays operable with any dialog open; off only
   * for a dialog that IS the call surface and would otherwise show the call twice.
   */
  layerSlot?: boolean;
}

const FRAME =
  'fixed z-dialog flex flex-col overflow-hidden border border-border bg-surface-raised text-fg shadow-lg';

/**
 * @internal Shared by `Modal` and `Drawer`, which differ only in geometry. It is one component
 * so that the accessibility contract — name, close button, focus restore — cannot drift
 * between them, which is exactly what happens when two files "just wrap Radix Dialog".
 */
export const DialogFrame = forwardRef<HTMLDivElement, DialogFrameProps>(function DialogFrame(
  {
    title,
    titleHidden = false,
    description,
    descriptionHidden = false,
    trigger,
    footer,
    closeLabel = 'Close',
    open,
    defaultOpen,
    onOpenChange,
    onOpenAutoFocus,
    onEscapeKeyDown,
    onInteractOutside,
    overlayClassName,
    bodyClassName,
    layerSlot = true,
    className,
    children,
    ...rest
  },
  ref,
) {
  // See §3. Populated in `onOpenAutoFocus`, which fires while the outgoing element still has
  // focus, and only ever read when there is no trigger for Radix to return to.
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  return (
    <Dialog.Root
      modal
      /**
       * Conditional spreads because of `exactOptionalPropertyTypes`: `open={undefined}` is a
       * different thing from an absent `open`, and passing the first is what turns an
       * uncontrolled dialog into a permanently-closed controlled one.
       */
      {...(open !== undefined ? { open } : {})}
      {...(defaultOpen !== undefined ? { defaultOpen } : {})}
      {...(onOpenChange !== undefined ? { onOpenChange } : {})}
    >
      {trigger ? <Dialog.Trigger asChild>{trigger}</Dialog.Trigger> : null}

      <Dialog.Portal>
        <Dialog.Overlay className={cn('fixed inset-0 z-dialog bg-canvas/80', overlayClassName)} />

        <Dialog.Content
          ref={ref}
          className={cn(FRAME, className)}
          onOpenAutoFocus={(event) => {
            const active = document.activeElement;
            restoreFocusRef.current = active instanceof HTMLElement ? active : null;
            onOpenAutoFocus?.(event);
          }}
          {...(onEscapeKeyDown !== undefined ? { onEscapeKeyDown } : {})}
          onInteractOutside={(event) => {
            /**
             * A toast is not "outside". The toast viewport keeps its pointer events while a
             * dialog is open (`Toast.tsx`), so a toast can be read and dismissed; a press on it
             * must not also close the dialog underneath, which is what Radix would do with any
             * pointer-down it does not find inside the content.
             */
            const target = event.target;
            if (target instanceof Element && target.closest('[data-pbx-toast-viewport]')) {
              event.preventDefault();
              return;
            }
            onInteractOutside?.(event);
          }}
          {...(trigger
            ? {}
            : {
                onCloseAutoFocus: (event: Event) => {
                  // Stops Radix's own handler, which would `preventDefault()` and then focus a
                  // trigger that does not exist. We prevent for the same reason it does — the
                  // browser's default restore would fight the explicit `focus()` — and then
                  // actually put focus somewhere.
                  event.preventDefault();
                  restoreFocusRef.current?.focus();
                },
              })}
          {...rest}
        >
          <div className="flex items-start gap-3 border-b border-border px-5 py-4">
            <div className="min-w-0 flex-1">
              <Dialog.Title
                className={cn('text-base font-semibold text-fg', titleHidden && 'sr-only')}
              >
                {title}
              </Dialog.Title>
              {description ? (
                <Dialog.Description
                  className={cn(
                    'mt-1 text-sm text-fg-muted',
                    descriptionHidden && 'sr-only',
                  )}
                >
                  {description}
                </Dialog.Description>
              ) : null}
            </div>

            {/**
             * First in tab order, which is a choice: it is the one control in a dialog that is
             * never destructive, so a user who lands on it and presses Enter gets the least
             * surprising outcome available. A dialog that wants focus on its first field says
             * so with `onOpenAutoFocus`, visibly, rather than by relying on DOM order.
             */}
            <Dialog.Close asChild>
              <Button
                variant="ghost"
                size="sm"
                className="-mr-2 shrink-0 px-2"
                iconLeft={<X className="size-4" />}
              >
                <span className="sr-only">{closeLabel}</span>
              </Button>
            </Dialog.Close>
          </div>

          {children ? (
            /**
             * `tabIndex={0}` on a plain `<div>` is deliberate and it is WCAG 2.1.1: a region
             * that scrolls but contains nothing focusable cannot be scrolled by keyboard at
             * all — the content below the fold is simply unreachable. Chrome now focuses such
             * scrollers natively; Safari and Firefox do not, so the attribute is what makes
             * the behaviour universal. The cost is one unnamed tab stop when the body happens
             * not to overflow, which is a smaller failure than unreachable text.
             *
             * `min-h-0` because a flex child's default `min-height: auto` refuses to shrink
             * below its content, so without it the panel grows past `max-h` and the footer is
             * pushed off screen instead of the body scrolling — the bug that looks like
             * `overflow-y-auto` "not working".
             */
            <div
              tabIndex={0}
              className={cn('min-h-0 flex-1 overflow-y-auto px-5 py-4', bodyClassName)}
            >
              {children}
            </div>
          ) : null}

          {/*
            The foot: the dialog's own actions, then — last in the panel and last in the tab
            order — the call strip, when the shell has a call to show and this is the top-most
            open dialog (`dialogLayer.tsx`). Inside `Dialog.Content` on purpose — that is the
            whole fix. `DialogFoot` also publishes its height as `--pbx-dialog-foot`, so the
            toasts stack above a dialog's primary action instead of on it.
          */}
          <DialogFoot footer={footer} layerSlot={layerSlot} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

export type ModalSize = 'sm' | 'md' | 'lg';

const MODAL_SIZES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
};

/**
 * `w-[calc(100%-2rem)]` and not a fixed width: on a fixed-position element a percentage
 * resolves against the viewport, so this is "the viewport less a 1rem gutter each side" and
 * the panel never overflows a 320px screen — WCAG 1.4.10 (Reflow). `max-h` uses `dvh` rather
 * than `vh` because mobile browser chrome makes `vh` taller than the space that is actually
 * visible, which puts the footer — the buttons — under the URL bar.
 */
const MODAL_FRAME =
  'left-1/2 top-1/2 w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-lg';

export interface ModalProps extends DialogFrameProps {
  size?: ModalSize;
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(function Modal(
  { size = 'md', className, ...rest },
  ref,
) {
  return (
    <DialogFrame ref={ref} className={cn(MODAL_FRAME, MODAL_SIZES[size], className)} {...rest} />
  );
});
